import type { ReactNode } from "react"
import { Link } from "@tanstack/react-router"

import { cn } from "@productlane/lib/src/cn"
import {
	COVERAGE_BANDS,
	coverageAdvice,
	coverageScore,
	coverageTierIndex,
	coverageTierLabel,
} from "@productlane/lib/src/knowledge-coverage"
import { Badge } from "@productlane/ui"

import { api } from "@/lib/api"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"

const num = (value: number) => value.toLocaleString("en-US")

const compact = (value: number) =>
	value >= 1000 ? `${Math.round(value / 1000)}k` : `${value}`

// One child, so SettingsContainer's divider has nothing to draw between the
// header row and the meter below it.
const Card = ({
	className,
	action,
	children,
}: {
	className?: string
	action?: ReactNode
	children: ReactNode
}) => (
	<SettingsContainer className={className}>
		<div>
			<SettingItem
				title="Knowledge coverage"
				description="How much the agent has to answer from"
				action={action}
			/>
			{children}
		</div>
	</SettingsContainer>
)

export function KnowledgeCoverageCard({ className }: { className?: string }) {
	const { data } = api.ai.getKnowledgeCoverage.useQuery(undefined, {
		staleTime: 5 * 60 * 1000,
	})

	// Placeholders keep the card at its final height, so the rows below it stay
	// put when the count lands.
	if (!data) {
		return (
			<Card className={className}>
				<div className="mt-4 animate-pulse">
					<div className="h-8 w-20 rounded bg-gray-100 dark:bg-gray-800" />
					<div className="mt-3 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800" />
					<div className="mt-1.5 h-3" />
					<div className="mt-4 border-t border-gray-300/80 pt-3 dark:border-gray-700/80">
						<div className="h-3 w-full rounded bg-gray-100 dark:bg-gray-800" />
						<div className="mt-1.5 h-3 w-2/3 rounded bg-gray-100 dark:bg-gray-800" />
					</div>
				</div>
			</Card>
		)
	}

	const { chars, articles } = data
	const score = coverageScore(chars)
	const bandIndex = coverageTierIndex(chars) - 1
	const advice = coverageAdvice(data)

	return (
		<Card
			className={className}
			action={
				<Badge variant={score >= 50 ? "green" : "default"}>
					{coverageTierLabel(chars)}
				</Badge>
			}
		>
			<div className="mt-4 flex items-end justify-between gap-4">
				<div className="flex items-baseline gap-1">
					<span className="text-[32px] font-medium tabular-nums leading-none text-primary">
						{score}
					</span>
					<span className="text-sm text-tertiary">/100</span>
				</div>
				<span className="text-[11px] tabular-nums text-tertiary">
					{num(chars)} characters in {num(articles)}{" "}
					{articles === 1 ? "article" : "articles"}
				</span>
			</div>

			<div className="relative mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
				<div
					className="h-full rounded-full bg-green-500 dark:bg-green-400"
					style={{ width: `${score}%` }}
				/>
				{[25, 50, 75].map((left) => (
					<span
						key={left}
						className="absolute top-0 h-full w-px bg-white dark:bg-gray-850"
						style={{ left: `${left}%` }}
					/>
				))}
			</div>
			<div className="mt-1.5 flex text-[10px] text-tertiary">
				{COVERAGE_BANDS.map((band, index) => (
					<span
						key={band.label}
						className={cn(
							"w-1/4 text-center",
							index === bandIndex && "text-primary",
						)}
					>
						{band.label} <span className="opacity-70">{compact(band.to)}</span>
					</span>
				))}
			</div>

			<p className="mt-4 border-t border-gray-300/80 pt-3 text-xs leading-relaxed text-secondary dark:border-gray-700/80">
				{advice.tip}{" "}
				<Link to={advice.to} className="text-primary underline">
					{advice.action}
				</Link>
			</p>
		</Card>
	)
}
